import { actionProps } from "@/lib/controlAnalytics";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useNavigationType, useSearchParams } from "react-router-dom";
import { CloseIcon, SearchIcon } from "@/components/icons";
import { isJobsPath, jobsPathWith } from "@/lib/routes";
import { isSearchShortcut } from "@/lib/shortcut";

/**
 * Search sits inside Jobs at every width.
 * Enter goes to Jobs; in a job view it edits that page's `q`.
 * `/` outside a field focuses the search.
 */
/** How long typing rests before the words go to the Jobs address. */
const ADDRESS_SETTLE_MS = 200;

export function GlobalSearch({
    inline = false,
    labelled = false,
    placeholder,
    onDraftChange,
}: {
    inline?: boolean;
    /** The front page's look: a visible Job title label over a taller field,
     * the same as the Location beside it (LocationSearch's `site`). */
    labelled?: boolean;
    /** What the empty box asks, where it searches less than everything. */
    placeholder?: string;
    onDraftChange?: (words: string) => void;
}) {
    const navigate = useNavigate();
    const navigation = useNavigationType();
    const { pathname } = useLocation();
    const [params, setParams] = useSearchParams();
    const onJobs = isJobsPath(pathname);
    const words = onJobs ? (params.get("q") ?? "") : "";
    // The box holds what is typed. On Jobs the words go to the address
    // once typing rests: the address updates a render later, and a box
    // that read its value back from it lost every key typed in between.
    const [draft, setDraft] = useState(words);
    const written = useRef(words);
    // What the box holds, for the effect below without running it each key.
    const typed = useRef(words);
    const value = draft;
    const input = useRef<HTMLInputElement>(null);

    // Words that reach the address from elsewhere (a link, Back, leaving
    // Jobs) replace the box; the echo of our own write does not. Nor does
    // the page writing its own address while words typed here wait to be
    // written: Jobs fills in the resume's latest title when it arrives, and
    // a resume that arrived mid-typing replaced the person's words with it.
    // The typed words win, and are written when typing rests.
    useEffect(() => {
        if (words === written.current) return;
        const waiting = typed.current !== written.current;
        written.current = words;
        if (navigation === "REPLACE" && waiting) return;
        typed.current = words;
        setDraft(words);
        onDraftChange?.(words);
    }, [words, navigation, onDraftChange]);

    useEffect(() => {
        if (!onJobs || draft === written.current) return;
        const timer = window.setTimeout(() => {
            written.current = draft;
            // From the address as it stands when the timer fires, filters and all.
            setParams(
                (current) => {
                    const nextParams = new URLSearchParams(current);
                    if (draft) nextParams.set("q", draft);
                    else nextParams.delete("q");
                    return nextParams;
                },
                { replace: true },
            );
        }, ADDRESS_SETTLE_MS);
        return () => window.clearTimeout(timer);
    }, [draft, onJobs, setParams]);

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (isSearchShortcut(event, event.target)) {
                event.preventDefault();
                input.current?.focus();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const change = (next: string) => {
        typed.current = next;
        setDraft(next);
        onDraftChange?.(next);
    };

    const submit = () => {
        if (onJobs || !draft.trim()) return;
        typed.current = "";
        setDraft("");
        navigate(jobsPathWith(new URLSearchParams({ q: draft.trim() })));
    };

    // Searching Jobs, the box is the page's own filter: ringed in the green,
    // with a clear in place of the shortcut.
    const active = onJobs && value !== "";

    const ring = active
        ? "border-green text-brand-ink shadow-[0_0_0_3px_var(--color-tint-green)]"
        : `${labelled ? "border-line-control" : "border-line-strong"} text-fg-faint focus-within:ring-focus`;
    const field = (
        <>
            <SearchIcon className="size-4" />
            <input
                {...actionProps("job_search_edited", { source: "Topbar.GlobalSearch" })}
                ref={input}
                type="search"
                value={value}
                onChange={(event) => change(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === "Enter") submit();
                }}
                placeholder={
                    placeholder ?? (labelled ? "Title or company" : "Search titles and companies")
                }
                aria-label="Search titles and companies"
                className={`min-w-0 flex-1 border-0 bg-transparent text-fg outline-none placeholder:text-fg-faint ${labelled ? "text-[15px]" : "text-[13px]"}`}
            />
            {active ? (
                <button
                    {...actionProps("job_search_cleared", { source: "Topbar.GlobalSearch" })}
                    type="button"
                    onClick={() => change("")}
                    aria-label="Clear search"
                    className="grid size-[22px] place-items-center rounded-md bg-card-raised text-fg-muted hover:text-fg"
                >
                    <CloseIcon className="size-3" strokeWidth={2.2} />
                </button>
            ) : (
                <kbd className="rounded-md border border-line px-1.5 py-0.5 font-mono text-[10.5px] text-fg-faint">
                    /
                </kbd>
            )}
        </>
    );

    if (labelled) {
        return (
            <label className="flex w-full min-w-0 flex-col gap-1.5 text-[15px] font-semibold text-fg">
                <span>Job title</span>
                <span
                    className={`search flex h-12 items-center gap-2.5 rounded-control border bg-card px-3.5 font-normal ${ring}`}
                >
                    {field}
                </span>
            </label>
        );
    }
    return (
        <label
            className={`search h-11 items-center gap-2.5 rounded-control border bg-card px-3.5 ${inline ? "flex w-full" : "hidden max-w-[520px] flex-[1_1_280px] md:flex"} ${ring}`}
        >
            {field}
        </label>
    );
}
